/**
 * Typed fetch wrapper.
 *
 * All requests include credentials (cookies) and go to the backend.
 * In development Vite proxies /api and /auth to localhost:8000.
 * In production set VITE_API_URL to the Render backend URL.
 *
 * CSRF: on startup (called from WarmingUpScreen flow) we fetch a CSRF token
 * from GET /api/v1/csrf-token.  Every state-changing request (POST/PUT/PATCH/
 * DELETE) sends it back in the X-CSRF-Token header.
 */

const BASE = import.meta.env.VITE_API_URL ?? ''

// Module-level CSRF token.  Set once after waitForBackend resolves.
let _csrfToken: string | null = null

export async function initCsrf(): Promise<void> {
  const res = await fetch(`${BASE}/api/v1/csrf-token`, { credentials: 'include' })
  if (res.ok) {
    const data = await res.json()
    _csrfToken = data.token ?? null
  }
}

interface ApiError {
  code: string
  message: string
  details?: unknown
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError,
  ) {
    super(error.message)
    this.name = 'ApiRequestError'
  }
}

export const NETWORK_ERROR_CODE = 'NETWORK_ERROR'
const NETWORK_ERROR_MESSAGE = "Can't reach the server. Check your connection and try again."

/**
 * fetch() rejects with a raw TypeError on network failure / CORS / backend-down.
 * Normalise that into an ApiRequestError (status 0) so callers can handle it the
 * same way as HTTP errors and show a distinct "offline" message + retry.
 */
async function _fetch(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init)
  } catch {
    throw new ApiRequestError(0, { code: NETWORK_ERROR_CODE, message: NETWORK_ERROR_MESSAGE })
  }
}

function _csrfHeaders(method: string): Record<string, string> {
  const mutating = ['POST', 'PUT', 'PATCH', 'DELETE']
  if (mutating.includes(method) && _csrfToken) {
    return { 'x-csrf-token': _csrfToken }
  }
  return {}
}

/** Return the CSRF header for callers that need to construct their own fetch (e.g. blob responses). */
export function csrfHeaders(): Record<string, string> {
  return _csrfToken ? { 'x-csrf-token': _csrfToken } : {}
}

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const res = await _fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ..._csrfHeaders(method),
      ...(init.headers ?? {}),
    },
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const apiError: ApiError = body?.error ?? {
      code: 'UNKNOWN',
      message: (body?.detail ?? res.statusText) || 'An unexpected error occurred',
    }
    // A mutating call can 403 on a CSRF mismatch when the token rotated (or was never
    // initialised on this tab). Refresh the token once and retry, rather than surfacing
    // "CSRF token mismatch" and forcing a manual reload.
    const looksCsrf = /csrf/i.test(apiError.message) || /csrf/i.test(apiError.code)
    if (res.status === 403 && looksCsrf && !retried && method !== 'GET') {
      await initCsrf()
      return request<T>(path, init, true)
    }
    throw new ApiRequestError(res.status, apiError)
  }

  // 204 No Content
  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

async function uploadFiles<T>(path: string, files: File[]): Promise<T> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }
  const res = await _fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ..._csrfHeaders('POST') },
    body: formData,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const apiError: ApiError = body?.error ?? {
      code: 'UNKNOWN',
      message: (body?.detail ?? res.statusText) || 'An unexpected error occurred',
    }
    throw new ApiRequestError(res.status, apiError)
  }
  return res.json() as Promise<T>
}

async function postForm<T>(path: string, formData: FormData): Promise<T> {
  const res = await _fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ..._csrfHeaders('POST') },
    body: formData,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const apiError: ApiError = body?.error ?? {
      code: 'UNKNOWN',
      message: (body?.detail ?? res.statusText) || 'An unexpected error occurred',
    }
    throw new ApiRequestError(res.status, apiError)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, files: File[]) => uploadFiles<T>(path, files),
  // Single multipart POST with a caller-built FormData (e.g. one "file" field).
  postForm: <T>(path: string, formData: FormData) => postForm<T>(path, formData),
}
